import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DashboardHeader from '../../components/Shop/Layout/DashboardHeader';
import DashboardSideBar from '../../components/Shop/Layout/DashboardSideBar';
import DualSponsoredDashboard from '../../components/Sponsored/DualSponsoredDashboard';
import AdTypeDistributionChart from '../../components/Sponsored/AdTypeDistributionChart';
import TopPerformingAds from '../../components/Sponsored/TopPerformingAds';
import { adsServer } from '../../server';
import CampaignCreateModal from '../../components/Sponsored/CampaignCreatorModal';
import Loader from '../../components/Layout/Loader';
import BentoEmptyState from '../../components/Layout/BentoEmptyState';
import { getStoredCurrentProfile, isShopProfile } from '../../utils/shopProfile';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { loadSeller } from '../../redux/actions/user';

const ShopCreateAdvertisement = () => {
  const [sponsoredAds, setSponsoredAds] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const currentProfile = getStoredCurrentProfile();
  const dispatch = useDispatch();
  const seller = useSelector((state) => state.seller.seller);
  const user = useSelector((state) => state.user.user);
  const navigate = useNavigate();

  useEffect(() => {
    if (!isShopProfile(currentProfile)) {
      setSponsoredAds([]);
      setIsLoading(false);
      return;
    }

    if (seller?.handle !== currentProfile && seller?.name !== currentProfile) {
      dispatch(loadSeller(currentProfile));
    }
  }, [currentProfile, dispatch, seller?.handle, seller?.name]);

  useEffect(() => {
    if (!isShopProfile(currentProfile)) {
      return;
    }

    const fetchSponsoredAdsAndMetrics = async () => {
      setIsLoading(true);
      try {
        const token = localStorage.getItem('token');

        // Fetch sponsored ads
        const response = await axios.get(`${adsServer}/sponsored/sponsored-ads`, {
          withCredentials: true,
          headers: {
            Authorization: `Bearer ${token}`,
            'current-profile': currentProfile,
          },
        });

        const sortedAds = response.data.sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );

        // Fetch metrics for all ads in a single request
        const adIds = sortedAds.map(ad => ad._id);
        if (!adIds.length) {
          setSponsoredAds([]);
          return;
        }

        const metricsData = await fetchAdMetricsBatch(adIds);

        // Combine ads with metrics
        const adsWithMetrics = sortedAds.map(ad => ({
          ...ad,
          metrics: metricsData[ad._id] || { clicks: [], viewedImpressions: [] },
        }));

        setSponsoredAds(adsWithMetrics);
      } catch (error) {
        console.error('Error fetching sponsored ads:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSponsoredAdsAndMetrics();
  }, [currentProfile]);

  const fetchAdMetricsBatch = async (adIds) => {
    if (!adIds.length) {
      return {};
    }

    try {
      const response = await axios.post(
        `${adsServer}/sponsored/metrics/batch`,
        { adIds },
        {
          withCredentials: true,
          headers: {
            'current-profile': currentProfile,
          },
        }
      );
      return response.data; // Response should contain metrics for all ad IDs
    } catch (error) {
      console.error('Error fetching metrics:', error);
      return [];
    }
  };

  const openModal = () => {
    if (user?.role === 'Admin') {
      navigate('/admin-ads');
      return;
    }

    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  if (!isShopProfile(currentProfile)) {
    return (
      <div className="shop-create-advertisement-campaign-container">
        <DashboardHeader />
        <div className="ad-dashboard-layout shop-management-layout">
          <div className="shop-management-sidebar-slot">
            <DashboardSideBar active={8} />
          </div>
          <main className="shop-create-advertisement-campaign-main shop-management-main-panel">
            <BentoEmptyState
              title="No shop profile selected"
              description="Select a shop profile before creating campaigns or loading ad analytics."
              actionLabel="Go To Shop Login"
              actionTo="/shop-login"
              tiles={[
                {
                  title: 'Ad Center',
                  description: 'Campaign management is tied to the active seller profile.',
                },
                {
                  title: 'Predictions',
                  description: 'Ad spend forecasting becomes available once the shop context is restored.',
                },
                {
                  title: 'Billing',
                  description: 'Campaign charges stay scoped to the selected seller account.',
                },
                {
                  title: 'Analytics',
                  description: 'Clicks, impressions, and performance history load after shop selection.',
                },
              ]}
            />
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="shop-create-advertisement-campaign-container">
      <DashboardHeader />
      <div className="ad-dashboard-layout shop-management-layout">
        <div className="shop-management-sidebar-slot">
          <DashboardSideBar active={8} />
        </div>
        <main className="shop-management-main-panel">
        <div className="shop-create-advertisement-campaign-content">
          {!isLoading && sponsoredAds.length === 0 ? (
            <BentoEmptyState
              title="No campaign data available"
              description="No sponsored ads exist for this shop yet. Start a campaign and analytics will populate here."
              actionLabel="Create Campaign"
              onAction={openModal}
              tiles={[
                {
                  title: 'Predictions',
                  description: 'Ad spend estimates will populate once campaign history exists.',
                },
                {
                  title: 'Products',
                  description: 'Product, shop, and brand ads can be configured from the same campaign flow.',
                },
                {
                  title: 'Metrics',
                  description: 'Clicks and viewed impressions will appear after your first ad run.',
                },
                {
                  title: 'Billing',
                  description: 'Campaign spend totals will sync into this dashboard once campaigns launch.',
                },
              ]}
            />
          ) : (
            <>
          <div className="shop-create-advertisement-campaign-sidebar">
            <div className="shop-create-advertisement-campaign-box">
              <button className="create-ad-button" onClick={openModal}>
                Create Campaign
              </button>

              {/* Modal component */}
              <CampaignCreateModal isOpen={isModalOpen} onClose={closeModal} />
            </div>

            {/* Only render these charts when NOT loading */}
            {!isLoading && (
              <>
                <AdTypeDistributionChart ads={sponsoredAds} />
                <TopPerformingAds ads={sponsoredAds} />
              </>
            )}
          </div>

          <div className="shop-create-advertisement-campaign-main">
            {!isLoading && (
              <DualSponsoredDashboard
                sponsoredAds={sponsoredAds}
                onUpdateAds={setSponsoredAds}
                isLoading={isLoading}
              />
            )}
          </div>
            </>
          )}
        </div>
        </main>
      </div>

      {/* Optional backdrop overlay if the modal is open */}
      {isModalOpen && <div className="mobile-background-overlay"></div>}

      {/* 2) Display Loader if isLoading === true */}
      {isLoading && <Loader />}
    </div>
  );
};

export default ShopCreateAdvertisement;


